import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { scopedProjectKey } from "@cmmd-forge/client-runtime";
import { BellRingIcon, CheckCircle2Icon, Loader2Icon, XCircleIcon, XIcon } from "lucide-react";
import { memo } from "react";

import { projectImportDestinationLabel, type ProjectImportJob } from "~/projectImportStore";
import { SidebarMenuItem } from "../ui/sidebar";
import { SidebarProjectItem, type SidebarProjectItemProps } from "./SidebarProjectItem";

function projectImportStatusLabel(job: ProjectImportJob): string {
  if (job.status === "ready") return "Ready";
  if (job.status !== "cloning") return job.source === "blank" ? "Create failed" : "Clone failed";
  if (job.source !== "blank") return "Cloning";
  return job.destination === "forge-cloud" ? "Provisioning" : "Creating";
}

function projectImportProgressLabel(job: ProjectImportJob): string | null {
  if (job.status !== "cloning" || job.percent === null) return null;
  return job.current !== null && job.total !== null
    ? `${job.percent}% (${job.current}/${job.total})`
    : `${job.percent}%`;
}

function projectImportIcon(job: ProjectImportJob) {
  if (job.status === "cloning" && job.actionRequired === true) return BellRingIcon;
  if (job.status === "cloning") return Loader2Icon;
  return job.status === "ready" ? CheckCircle2Icon : XCircleIcon;
}

function projectImportTone(job: ProjectImportJob): "action" | "working" | "ready" | "failed" {
  if (job.status === "cloning" && job.actionRequired === true) return "action";
  if (job.status === "cloning") return "working";
  return job.status === "ready" ? "ready" : "failed";
}

const ICON_TONE_CLASS = {
  action: "text-warning",
  working: "text-muted-foreground/70",
  ready: "text-success",
  failed: "text-destructive",
} as const;

const PILL_TONE_CLASS = {
  action: "border-warning-border text-warning-foreground",
  working: "border-border/60 text-muted-foreground/75",
  ready: "border-success-border text-success",
  failed: "border-destructive-border text-destructive",
} as const;

interface ProjectImportJobViewModel {
  readonly Icon: ReturnType<typeof projectImportIcon>;
  readonly rowClass: string;
  readonly iconClass: string;
  readonly iconAnimationClass: string;
  readonly subtext: string;
  readonly subtextClass: string;
  readonly pillLabel: string;
  readonly pillClass: string;
  readonly ariaBusy: true | undefined;
  readonly ariaLive: "polite" | undefined;
  readonly showProgress: boolean;
  readonly progressAriaLabel: string;
  readonly progressClass: string;
  readonly progressStyle: { readonly width: string } | undefined;
  readonly showDismiss: boolean;
}

interface ProjectImportProgressModel {
  readonly show: boolean;
  readonly ariaLabel: string;
  readonly className: string;
  readonly style: { readonly width: string } | undefined;
}

function projectImportProgressModel(job: ProjectImportJob): ProjectImportProgressModel {
  const show = job.status === "cloning" && job.actionRequired !== true;
  const hasPercent = job.status === "cloning" && job.percent !== null;
  return {
    show,
    ariaLabel: `${job.label} ${job.source === "blank" ? "creation" : "clone"} progress`,
    className: hasPercent ? "" : "w-2/3 motion-safe:animate-status-pulse",
    style: hasPercent ? { width: `${job.percent}%` } : undefined,
  };
}

function projectImportIconAnimation(tone: ReturnType<typeof projectImportTone>): string {
  if (tone === "action") return "motion-safe:animate-status-pulse";
  return tone === "working" ? "animate-spin" : "";
}

function projectImportSubtext(job: ProjectImportJob, actionRequired: boolean): string {
  const detail = job.error ?? job.detail;
  if (actionRequired) return detail;
  return `${projectImportDestinationLabel(job.destination)} - ${detail}`;
}

function projectImportPillLabel(job: ProjectImportJob, actionRequired: boolean): string {
  if (actionRequired) return "Action needed";
  return projectImportProgressLabel(job) ?? projectImportStatusLabel(job);
}

function projectImportJobViewModel(job: ProjectImportJob): ProjectImportJobViewModel {
  const isCloning = job.status === "cloning";
  const actionRequired = isCloning && job.actionRequired === true;
  const tone = projectImportTone(job);
  const progress = projectImportProgressModel(job);
  return {
    Icon: projectImportIcon(job),
    rowClass: actionRequired ? "bg-warning-surface ring-1 ring-inset ring-warning-border" : "",
    iconClass: ICON_TONE_CLASS[tone],
    iconAnimationClass: projectImportIconAnimation(tone),
    subtext: projectImportSubtext(job, actionRequired),
    subtextClass: actionRequired
      ? "whitespace-normal break-words text-muted-foreground"
      : "truncate text-muted-foreground/65",
    pillLabel: projectImportPillLabel(job, actionRequired),
    pillClass: PILL_TONE_CLASS[tone],
    ariaBusy: isCloning ? true : undefined,
    ariaLive: actionRequired ? "polite" : undefined,
    showProgress: progress.show,
    progressAriaLabel: progress.ariaLabel,
    progressClass: progress.className,
    progressStyle: progress.style,
    showDismiss: !isCloning,
  };
}

export const ProjectImportJobRow = memo(function ProjectImportJobRow({
  job,
  onDismiss,
}: {
  readonly job: ProjectImportJob;
  readonly onDismiss: () => void;
}) {
  const model = projectImportJobViewModel(job);
  const Icon = model.Icon;

  return (
    <SidebarMenuItem data-testid="project-import-job">
      <div
        className={`group/import-row grid min-h-9 w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-md px-2 py-1.5 text-left ${model.rowClass}`}
        aria-busy={model.ariaBusy}
        aria-live={model.ariaLive}
      >
        <span
          className={`inline-flex size-4 shrink-0 items-center justify-center ${model.iconClass}`}
        >
          <Icon className={`size-3.5 ${model.iconAnimationClass}`} />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-xs font-medium text-foreground">{job.label}</span>
          <span className={`block text-[10px] ${model.subtextClass}`} title={model.subtext}>
            {model.subtext}
          </span>
          {model.showProgress ? (
            <span
              className="mt-1 block h-1.5 overflow-hidden rounded-full bg-muted"
              aria-label={model.progressAriaLabel}
            >
              <span
                className={`block h-full rounded-full bg-primary/70 ${model.progressClass}`}
                style={model.progressStyle}
              />
            </span>
          ) : null}
        </span>
        <span className="flex min-w-0 items-center gap-1">
          <span
            className={`whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px] ${model.pillClass}`}
          >
            {model.pillLabel}
          </span>
          {model.showDismiss ? (
            <button
              type="button"
              aria-label={`Dismiss ${job.label} import status`}
              className="inline-flex size-5 items-center justify-center rounded-md text-muted-foreground/50 hover:bg-secondary hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
              onClick={onDismiss}
            >
              <XIcon className="size-3" />
            </button>
          ) : null}
        </span>
      </div>
    </SidebarMenuItem>
  );
});

type SortableProjectRowProps = Omit<SidebarProjectItemProps, "dragHandleProps">;

export const SortableProjectRow = memo(function SortableProjectRow(props: SortableProjectRowProps) {
  const { project, isManualProjectSorting } = props;
  const { attributes, listeners, setActivatorNodeRef, setNodeRef, transform, transition } =
    useSortable({
      id: project.projectKey,
      disabled: !isManualProjectSorting,
      data: {
        sidebarProjectMemberKeys: project.memberProjectRefs.map(scopedProjectKey),
      },
    });
  const style = { transform: CSS.Translate.toString(transform), transition };

  return (
    <div ref={setNodeRef} style={style}>
      <SidebarProjectItem
        {...props}
        dragHandleProps={{ attributes, listeners, setActivatorNodeRef }}
      />
    </div>
  );
});
